import { Spinner, toast } from '@aus-platform/design-system';
import React, { useState } from 'react';
import { useSSOEnforcement } from 'shared/api';
import { isSsoLoginMode } from 'shared/helpers';
import { useDashboardCategoryConfig } from 'shared/hooks';
import { SSO_ENFORCEMENT_MESSAGES } from '../constants';
import { DashboardConfigurationsEdit } from './dashboard-configuration';
import { IterationLabelsEdit } from './iteration-labels';
import { OrgControlsMain } from './org-controls-main';
import { SSOEnforcementEdit } from './sso-enforcement';

enum OrgControlsView {
  Main = 'main',
  EditSsoEnforcement = 'edit-sso-enforcement',
  EditDashboardConfigurations = 'edit-dashboard-configurations',
  EditIterationLabels = 'edit-iteration-labels',
}

type OrgControlsTabProps = {
  onUnsavedChanges: (hasChanges: boolean) => void;
  onShowUnsavedBanner: VoidFunction;
};

export const OrgControlsTab: React.FC<OrgControlsTabProps> = ({
  onUnsavedChanges,
  onShowUnsavedBanner,
}) => {
  // States.
  const [view, setView] = useState<OrgControlsView>(OrgControlsView.Main);

  // Constants.
  const isSsoEnabled = isSsoLoginMode();

  // Hooks.

  const {
    data: enforcementResponse,
    isLoading: isSsoEnforcementLoading,
    isError: isSsoEnforcementError,
  } = useSSOEnforcement(isSsoEnabled);

  const { refetchDashboardConfig, isLoading: isDashboardConfigLoading } =
    useDashboardCategoryConfig();

  const config = enforcementResponse?.data;

  const isWaitingForOrgControls =
    (isSsoEnabled && isSsoEnforcementLoading) || isDashboardConfigLoading;

  const hasSsoEnforcementError =
    isSsoEnabled && (isSsoEnforcementError || !config);

  // Handlers
  const handleSaveSuccess = () => {
    setView(OrgControlsView.Main);
    toast.success(SSO_ENFORCEMENT_MESSAGES.SaveSuccess);
  };

  const handleDashboardConfigSaveSuccess = () => {
    setView(OrgControlsView.Main);
    toast.success('Dashboard configurations updated successfully.');
  };

  const handleEditDashboardConfigurations = async () => {
    try {
      await refetchDashboardConfig();
      setView(OrgControlsView.EditDashboardConfigurations);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load dashboard configurations.');
    }
  };

  if (isWaitingForOrgControls) {
    return (
      <div className="settings-content">
        <div className="settings-content__body org-controls-tab__loading">
          <Spinner />
        </div>
      </div>
    );
  }

  if (hasSsoEnforcementError) {
    return (
      <div className="settings-content">
        <div className="settings-content__body org-controls-tab">
          <h3>Failed to load SSO enforcement settings.</h3>
        </div>
      </div>
    );
  }

  if (view === OrgControlsView.EditSsoEnforcement && config) {
    return (
      <SSOEnforcementEdit
        currentConfig={config}
        onCancel={() => setView(OrgControlsView.Main)}
        onSaveSuccess={handleSaveSuccess}
        onUnsavedChanges={onUnsavedChanges}
        onShowUnsavedBanner={onShowUnsavedBanner}
      />
    );
  }

  if (view === OrgControlsView.EditDashboardConfigurations) {
    return (
      <DashboardConfigurationsEdit
        onNavigateBack={() => setView(OrgControlsView.Main)}
        onSaveSuccess={handleDashboardConfigSaveSuccess}
        onUnsavedChanges={onUnsavedChanges}
      />
    );
  }

  if (view === OrgControlsView.EditIterationLabels) {
    return (
      <IterationLabelsEdit
        onNavigateBack={() => setView(OrgControlsView.Main)}
      />
    );
  }

  return (
    <OrgControlsMain
      config={config}
      onEditSsoEnforcement={() => setView(OrgControlsView.EditSsoEnforcement)}
      onEditDashboardConfigurations={handleEditDashboardConfigurations}
      onEditIterationLabels={() =>
        setView(OrgControlsView.EditIterationLabels)
      }
    />
  );
};
